import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ErpSyncHeldPeriod, ErpSyncPeriodRow, ErpSyncShownPeriod } from './erp-sync.types';

const NOTHING = 0;
const ONE = 1;
const ISO_DATE_LENGTH = 'YYYY-MM-DD'.length;

/** One editable row: two dates, or two nulls while nothing has been picked. */
type PeriodForm = FormGroup<{
    start: FormControl<Date | null>;
    end: FormControl<Date | null>;
}>;

/**
 * The ERP pull, with the comparison periods to fetch alongside it — a pure view.
 *
 * WHY THIS IS A DIALOG AND NOT A BUTTON. Only three of the four vendors can enumerate their own
 * fiscal years; Abacus cannot. So nothing on the server can answer "which years exist" for every
 * client, and a button that silently guessed the ranges would guess wrong exactly where it matters:
 * a shortened fiscal year, or one whose balance-sheet date moved. A guess that overlapped two real
 * years would come back with postings — the WRONG postings — and look like a valid comparison
 * column. Here the guess is visible and can be corrected before anything is pulled.
 *
 * The dialog shows three things, and the split is the point:
 *
 *   - the closing's own period, which is always pulled because it still changes;
 *   - the periods already stored, which are never pulled again;
 *   - the rows to fetch, pre-filled by stepping one year back and editable.
 *
 * The rows live in a form inside this view. That is input state, not domain state — the container
 * hears about it once, when the pull is started.
 */
@Component({
    selector: 'abcp-erp-sync',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './erp-sync.component.html',
    styleUrls: ['./erp-sync.component.scss'],
})
export class ErpSyncComponent implements OnChanges {
    /** The closing's own period, already formatted. Null when the screen does not know it yet. */
    @Input() closingPeriodLabel: ErpSyncShownPeriod | null = null;

    /** Periods a journal already exists for. Shown so the user can see there is nothing to do. */
    @Input() held: readonly ErpSyncHeldPeriod[] = [];

    /** The rows to start from. Applied when they change, so a reopened dialog is not stale. */
    @Input() suggested: readonly ErpSyncPeriodRow[] = [];

    /**
     * How many rows this dialog may hold — what is left before the closing's period ceiling.
     *
     * `0` is a meaningful value and not a misconfiguration: the closing already holds as many periods
     * as the income statement can show, so there is nothing left to fetch. The add button disappears
     * and the empty state says why.
     */
    @Input() maxRows = 4;

    /** A pull is running: the button waits rather than starting a second one. */
    @Input() saving = false;

    /** A message key from the last failed pull, or null. The container's pipe renders it. */
    @Input() error: string | null = null;

    @Output() start = new EventEmitter<ErpSyncPeriodRow[]>();
    @Output() closed = new EventEmitter<void>();

    readonly rows = new FormArray<PeriodForm>([]);

    /** The content the rows were last seeded from, so an unchanged suggestion does not reseed them. */
    private seededFrom: string | null = null;

    public constructor(private readonly cdr: ChangeDetectorRef) {
        // Picking a date changes a form value, not an input. Under `OnPush` that leaves `disabled`
        // showing what it showed before — the save button would stay locked after the user completed
        // the last row. The subscription lives as long as the form it listens to, so it needs no
        // teardown: both are collected together.
        this.rows.valueChanges.subscribe(() => this.cdr.markForCheck());
    }

    /**
     * Seed the rows from `suggested` — and only when its CONTENT changed, not its reference.
     *
     * The container rebuilds the suggestion array whenever the period list or the closing's period
     * emits, which is a new reference every time. Reseeding on that would wipe dates the user has
     * typed while the dialog is open, silently and mid-edit.
     */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['suggested'] === undefined) {
            return;
        }

        const next = ErpSyncComponent.fingerprintOf(this.suggested.slice(NOTHING, this.maxRows));
        if (next === this.seededFrom) {
            return;
        }

        this.seededFrom = next;
        this.rows.clear();
        for (const row of this.suggested.slice(NOTHING, this.maxRows)) {
            this.rows.push(ErpSyncComponent.rowFor(row));
        }

        // The rows live in a `FormArray`, which is not an input. Under `OnPush` a mutation of it is
        // invisible to this view: the input change marks the component, the hook reseeds, and the
        // template still renders the row count it saw last. That produced the exact bug this dialog
        // was built to avoid — the suggestions were computed, and the screen said there were none.
        this.cdr.markForCheck();
    }

    get canAddRow(): boolean {
        return this.rows.length < this.maxRows;
    }

    /** "Periode hinzufügen" was pressed with no room left — says why nothing was added. */
    limitReached = false;

    /**
     * Why there is nothing to fetch — and the two reasons are different.
     *
     * Three of them. The ceiling is reached, so nothing may be added at all. Or nothing is stored yet
     * — a first sync, with no period to step back from, and the closing's own period is the whole job.
     * Or periods are stored and the rows were removed by hand, and the next step is to add one.
     */
    get emptyMessageKey(): string {
        // The ceiling first: with no room left, "add one" is not the next step and offering it would
        // be a dead end.
        if (this.maxRows === NOTHING) {
            return 'abcp.accountSurface.syncMaxPeriodsReached';
        }

        return this.held.length === NOTHING
            ? 'abcp.accountSurface.syncNothingHeldYet'
            : 'abcp.accountSurface.syncNoRowsChosen';
    }

    /**
     * Which rows will actually be pulled.
     *
     * A row counts only when BOTH bounds are set and the start is not after the end. Incomplete rows
     * are what disables the button rather than being dropped quietly — a half-typed range that is
     * ignored is how someone believes they fetched a year they did not.
     */
    get complete(): ErpSyncPeriodRow[] {
        return this.rows.controls
            .map((row) => ({ start: row.controls.start.value, end: row.controls.end.value }))
            .filter((row): row is { start: Date; end: Date } => row.start !== null && row.end !== null)
            .map((row) => ({ periodStart: isoOf(row.start), periodEnd: isoOf(row.end) }))
            .filter((row) => row.periodStart <= row.periodEnd);
    }

    /** Any row started and not finished. The button waits for it rather than skipping it. */
    get hasIncompleteRow(): boolean {
        return this.rows.length > this.complete.length;
    }

    get disabled(): boolean {
        return this.saving || this.hasIncompleteRow;
    }

    addRow(): void {
        this.limitReached = !this.canAddRow;
        if (!this.limitReached) {
            this.rows.push(ErpSyncComponent.rowFor({ periodStart: '', periodEnd: '' }));
        }
        this.cdr.markForCheck();
    }

    removeRow(index: number): void {
        this.rows.removeAt(index);
        this.limitReached = false;
        this.cdr.markForCheck();
    }

    onStart(): void {
        if (this.disabled) {
            return;
        }

        this.start.emit(this.complete);
    }

    onClose(): void {
        this.closed.emit();
    }

    /** Trivial, and it keeps the template free of an index expression that shifts on removal. */
    positionOf(index: number): number {
        return index + ONE;
    }

    /** Content, not reference: the same periods in the same order are the same suggestion. */
    private static fingerprintOf(rows: readonly ErpSyncPeriodRow[]): string {
        return rows.map((row) => `${row.periodStart}..${row.periodEnd}`).join('|');
    }

    private static rowFor(row: ErpSyncPeriodRow): PeriodForm {
        return new FormGroup({
            start: new FormControl<Date | null>(dateOf(row.periodStart)),
            end: new FormControl<Date | null>(dateOf(row.periodEnd)),
        });
    }
}

/** ISO `YYYY-MM-DD` from a picked date. UTC, because the picker normalises to UTC midnight. */
function isoOf(value: Date): string {
    return value.toISOString().slice(NOTHING, ISO_DATE_LENGTH);
}

/** A picked date from ISO bounds, or null for the empty string a blank row carries. */
function dateOf(value: string): Date | null {
    return value.length === NOTHING ? null : new Date(`${value}T00:00:00.000Z`);
}
